
import { makeAutoObservable, runInAction } from 'mobx';
import axios from 'axios';
import { format, addDays, addSeconds, startOfMonth } from 'date-fns';
import ru from 'date-fns/locale/ru';
import { getAPIUrl } from '../api/apiUtils';
import { regionsStore } from '../stores';
import { toJS } from 'mobx';
import { groupIdToTitle } from '../utils/groupTitles';
import { EMOTIVE_THEMES, DEFAULT_THEMES, THEME_LABEL_BY_KEY } from '../utils/themes';

// Функция для преобразования времени в формат 'YYYY-MM-DD'
// Поддерживает как Unix timestamp в секундах, так и ISO-строку
function formatDate(timestamp) {
  if (timestamp == null) return null;

  let date;
  if (typeof timestamp === 'number') {
    // старый формат: секунды
    date = new Date(timestamp * 1000);
  } else {
    // новый формат: ISO-строка, например "2025-01-18T14:45:33+00:00"
    date = new Date(timestamp);
  }

  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().split('T')[0];
}

// Функция для группировки и преобразования данных с усреднением по датам
function transformData(inputData, selectedThemes, groupMode, selectedTheme) {
  if (!Array.isArray(inputData)) {
    console.error('Ошибка: inputData не является массивом', inputData);
    return [];
  }

  if (groupMode === 'sum') {
    // группировка по темам
    const groupedByTheme = selectedThemes.reduce((acc, theme) => {
      acc[theme] = {};
      return acc;
    }, {});

    inputData.forEach(item => {
      if (!item || !item.date) {
        console.warn('Некорректный элемент данных:', item);
        return;
      }

      const formattedDate = formatDate(item.date);
      selectedThemes.forEach(theme => {
        if (item[theme] !== undefined) {
          if (!groupedByTheme[theme][formattedDate]) {
            groupedByTheme[theme][formattedDate] = { sum: 0, count: 0 };
          }
          groupedByTheme[theme][formattedDate].sum += item[theme];
          groupedByTheme[theme][formattedDate].count += 1;
        }
      });
    });

    return Object.keys(groupedByTheme).map(theme => ({
      data: Object.keys(groupedByTheme[theme]).map(date => ({
        date,
        value: groupedByTheme[theme][date].sum / groupedByTheme[theme][date].count
      })),
      marker: theme
    }));
  } else {
    // группировка по group_id
    const groupedByGroupId = inputData.reduce((acc, item) => {
      if (!item || !item.group_id || !item.date || item[selectedTheme] === undefined) {
        console.warn('Некорректный элемент данных:', item);
        return acc;
      }

      const { group_id, date, [selectedTheme]: value } = item;
      const formattedDate = formatDate(date);

      if (!acc[group_id]) {
        acc[group_id] = {};
      }
      if (!acc[group_id][formattedDate]) {
        acc[group_id][formattedDate] = { sum: 0, count: 0 };
      }
      acc[group_id][formattedDate].sum += value;
      acc[group_id][formattedDate].count += 1;

      return acc;
    }, {});

    return Object.keys(groupedByGroupId).map(group_id => {
      const title = groupIdToTitle[group_id] || group_id.toString();
      return {
        data: Object.keys(groupedByGroupId[group_id]).map(date => ({
          date,
          value: groupedByGroupId[group_id][date].sum / groupedByGroupId[group_id][date].count
        })),
        marker: title
      };
    });
  }
}

function getVkPostLink(groupId, postId) {
  if (groupId == null || postId == null) return null;
  const numericGroupId = Number(groupId);
  if (Number.isNaN(numericGroupId)) return null;
  const vkGroupId = numericGroupId > 0 ? -numericGroupId : numericGroupId;
  return `https://vk.com/wall${vkGroupId}_${postId}`;
}

function getIntervalLabel(dateObj, aggregation) {
  if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) return null;
  if (aggregation === 7) {
    const weekStart = new Date(dateObj);
    const day = weekStart.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    weekStart.setDate(weekStart.getDate() + diff);
    const weekEnd = addDays(weekStart, 6);
    return `(${format(weekStart, 'yyyy-MM-dd', { locale: ru })} - ${format(weekEnd, 'yyyy-MM-dd', { locale: ru })})`;
  }
  if (aggregation === 30) {
    const monthStart = startOfMonth(dateObj);
    return format(monthStart, 'LLLL yyyy', { locale: ru });
  }
  return format(dateObj, 'yyyy-MM-dd', { locale: ru });
}

function buildModalRow(item, selectedThemes, selectedTheme, groupMode) {
  if (!item) return null;
  const timestamp = item.date;
  const dateObj =
    typeof timestamp === 'number'
      ? new Date(timestamp * 1000)
      : new Date(timestamp);
  if (Number.isNaN(dateObj.getTime())) return null;

  const formattedDate = format(dateObj, 'yyyy-MM-dd', { locale: ru });
  const groupId = item.group_id != null ? String(item.group_id) : '-';
  const postId = item.post_id ?? '-';
  const commentId = item.comment_id ?? '-';
  const authorId = item.author_id ?? '-';
  const groupTitle = groupIdToTitle[groupId] || groupId;

  const link = getVkPostLink(groupId, postId);
  const commentLink =
    link && commentId !== '-' ? `${link}?reply=${commentId}` : null;

  const row = {
    'Дата': formattedDate,
    // 'ID группы': groupId,
    'Название сообщества': groupTitle,
    'ID поста': postId,
    'ID комментария': commentId,
    // 'ID автора': authorId,
    'Автор': authorId !== '-' ? `https://vk.com/id${authorId}` : '-',
    'Текст': item.text ?? '-',
    'Ссылка на пост': link || '-',
    'Ссылка на комментарий': commentLink || '-',
  };

  const setThemeValue = (themeKey) => {
    if (item[themeKey] === undefined) return;
    const label = THEME_LABEL_BY_KEY[themeKey] || themeKey;
    row[label] = item[themeKey];
  };

  if (groupMode === 'sum') {
    selectedThemes.forEach(setThemeValue);
  } else if (selectedTheme && item[selectedTheme] !== undefined) {
    setThemeValue(selectedTheme);
  }

  EMOTIVE_THEMES.forEach(({ key }) => {
    if (row[THEME_LABEL_BY_KEY[key]] === undefined && item[key] !== undefined) {
      setThemeValue(key);
    }
  });

  return row;
}

function buildTableRowsByDate(data, aggregation, selectedThemes, selectedTheme, groupMode) {
  const result = {};
  if (!Array.isArray(data)) return result;

  data.forEach((item) => {
    if (!item || item.date == null) return;
    const timestamp = item.date;
    const dateObj =
      typeof timestamp === 'number'
        ? new Date(timestamp * 1000)
        : new Date(timestamp);
    if (Number.isNaN(dateObj.getTime())) return;

    const label = getIntervalLabel(dateObj, aggregation);
    if (!label) return;

    const row = buildModalRow(item, selectedThemes, selectedTheme, groupMode);
    if (!row) return;

    if (!result[label]) {
      result[label] = [];
    }
    result[label].push(row);
  });

  return result;
}

class EmotiveTrendsStore {
  aggregation = 1;
  selectedThemes = DEFAULT_THEMES.slice();
  groupMode = 'sum';
  selectedTheme = EMOTIVE_THEMES[0].key;
  chartsData = [];
  loading = true;
  error = null;
  globalFilters = { selectedGroups: [], startDate: null, endDate: null };
  globalGroups = [];
  lastQueryStartDate = null;
  lastQueryEndDate = null;
  lastQueryAggregation = null;
  lastQueryGroups = [];
  lastQueryThemes = [];
  lastQueryGroupMode = null;
  lastEndQuery = null;
  tableDataByDate = {};

  constructor() {
    makeAutoObservable(this);
  }

  setAggregation(val) { this.aggregation = val; }
  setThemes(val) { this.selectedThemes = val; }
  setGroupMode(val) { this.groupMode = val; }
  setSelectedTheme(val) { this.selectedTheme = val; }
  setGlobalFilters(val) { this.globalFilters = val; }
  setGlobalGroups(val) { this.globalGroups = val; }

  setLastQueryParams({ startDate, endDate, aggregation, selectedGroups, selectedThemes, groupMode }) {
    this.lastQueryStartDate = startDate;
    this.lastQueryEndDate = endDate;
    this.lastQueryAggregation = aggregation;
    this.lastQueryGroups = selectedGroups;
    this.lastQueryThemes = selectedThemes;
    this.lastQueryGroupMode = groupMode;
  }

  async fetchGroups() {
    try {
      await regionsStore.fetchRegions();
      const allGroups = regionsStore.allGroups.map(g => ({
        id: String(g.group_id),
        name: g.title
      }));
      runInAction(() => { this.globalGroups = allGroups; });
    } catch {
      runInAction(() => { this.globalGroups = []; });
    }
  }

  async fetchTopicsTrendsData(start, end) {
    // console.log('num_start:', start, 'num_end:', end);

    // Собираем текущие параметры
    const currentParams = {
      startDate: new Date(this.globalFilters.startDate),
      endDate: new Date(this.globalFilters.startDate),
      aggregation: this.aggregation,
      selectedGroups: this.globalFilters.selectedGroups.slice(),
      selectedThemes: this.selectedThemes.slice(),
      groupMode: this.groupMode
    };

    // Проверяем, изменились ли параметры
    const changed =
      this.lastQueryAggregation !== currentParams.aggregation ||
      JSON.stringify(this.lastQueryGroups) !== JSON.stringify(currentParams.selectedGroups) ||
      JSON.stringify(this.lastQueryThemes) !== JSON.stringify(currentParams.selectedThemes) ||
      this.lastQueryGroupMode !== currentParams.groupMode ||
      new Date(this.lastQueryStartDate).getTime() !== currentParams.startDate.getTime() ||
      new Date(this.lastQueryEndDate).getTime() !== currentParams.endDate.getTime();

    if (changed) {
      runInAction(() => {
        this.chartsData = []; // Сброс при изменении параметров
        this.lastEndQuery = null;
        this.tableDataByDate = {};
        // console.log('СБРОС');
        this.loading = true;
      });
    }

    // Определяем startDate с учётом lastEndQuery
    let startDate = new Date(currentParams.startDate);
    startDate.setDate(startDate.getDate() + Number(start * this.aggregation));

    let endDate = new Date(currentParams.startDate);
    
    endDate.setDate(endDate.getDate() + Number(end * this.aggregation) + 200);

    if (this.globalFilters.endDate && endDate.getTime() > new Date(this.globalFilters.endDate).getTime()) {
      endDate = new Date(this.globalFilters.endDate);
    }
    

    runInAction(() => {
      this.error = null;
    });

    try {
      const { selectedGroups } = this.globalFilters;
      if (!selectedGroups.length || !startDate || !endDate) {
        runInAction(() => {
          this.chartsData = [];
        });
        // console.log('return1')
        return;
      }

      let intervals = [];
      let currentDate = new Date(startDate);
      const endDateObj = new Date(endDate);
      while (currentDate <= endDateObj) {
        const intervalEnd = addSeconds(addDays(currentDate, this.aggregation), -1);
        const effectiveEnd = intervalEnd > endDateObj ? endDateObj : intervalEnd;
        intervals.push({
          start: Math.floor(currentDate.getTime() / 1000),
          end: Math.floor(effectiveEnd.getTime() / 1000),
          label:
            this.aggregation === 1
              ? format(currentDate, 'yyyy-MM-dd', { locale: ru })
              : `${format(currentDate, 'yyyy-MM-dd', { locale: ru })} - ${format(effectiveEnd, 'yyyy-MM-dd', { locale: ru })}`,
        });
        currentDate = addDays(currentDate, this.aggregation);
      }

      const aggregatedData = {};
      const aggregatedCounts = {};
      if (this.groupMode === 'sum') {
        intervals.forEach(interval => {
          aggregatedData[interval.label] = {};
          aggregatedCounts[interval.label] = {};
          this.selectedThemes.forEach(theme => {
            aggregatedData[interval.label][theme] = 0;
            aggregatedCounts[interval.label][theme] = 0;
          });
        });
      } else {
        selectedGroups.forEach(groupId => {
          aggregatedData[groupId] = {};
          aggregatedCounts[groupId] = {};
          intervals.forEach(interval => {
            aggregatedData[groupId][interval.label] = {};
            aggregatedCounts[groupId][interval.label] = {};
            aggregatedData[groupId][interval.label][this.selectedTheme] = 0;
            aggregatedCounts[groupId][interval.label][this.selectedTheme] = 0;
          });
        });
      }
      if (format(new Date(this.lastEndQuery ? this.lastEndQuery : startDate), 'yyyy-MM-dd') >= format(new Date(endDate), 'yyyy-MM-dd')){
        // console.log('return2')
        return;
      }
      else{
        this.loading = true;
      }
      const baseColumns = ['group_id', 'post_id', 'comment_id', 'author_id', 'date', 'text'];

      const dynamicColumns =
        this.groupMode === 'sum'
          ? [...this.selectedThemes, 'date']
          : [this.selectedTheme, 'date', 'group_id'];

      // Объединяем и убираем дубликаты
      const allColumns = Array.from(new Set([...baseColumns, ...dynamicColumns]));

      const params = {
        group_ids: selectedGroups.join(','),
        sort_by: 'date',
        sort_order: 'asc',
        columns: allColumns.join(','),
        start_date: format(
          new Date(this.lastEndQuery ? this.lastEndQuery : new Date(this.globalFilters.startDate)),
          'yyyy-MM-dd'
        ),
        end_date: format(new Date(endDate), 'yyyy-MM-dd'),
        limit: 10000000
      };

      // console.log('Отправляем запрос, параметры:', params);
      const response = await axios.get(`${getAPIUrl()}/detection/text_markers_analysis`, { params });


      const data = response.data?.data || [];

      console.log("result: ",data)
      
      const rowsByDate = buildTableRowsByDate(
        data,
        this.aggregation,
        this.selectedThemes,
        this.selectedTheme,
        this.groupMode
      );

      // Преобразуем данные и сохраняем в chartsData
      let transformedData = transformData(data, this.selectedThemes, this.groupMode, this.selectedTheme);
      if (this.aggregation === 7) {
        transformedData = transformedData.map(series => {
          const weeklyData = {};
          series.data.forEach(point => {
            const date = new Date(point.date);
            // Определяем начало недели (понедельник)
            const weekStart = new Date(date);
            weekStart.setDate(date.getDate() - date.getDay() + (date.getDay() === 0 ? -6 : 1));
            const weekEnd = addDays(weekStart, 6);
            const weekLabel = `(${format(weekStart, 'yyyy-MM-dd', { locale: ru })} - ${format(weekEnd, 'yyyy-MM-dd', { locale: ru })})`;

            if (!weeklyData[weekLabel]) {
              weeklyData[weekLabel] = { sum: 0, count: 0 };
            }
            weeklyData[weekLabel].sum += point.value;
            weeklyData[weekLabel].count += 1;
          });

          return {
            ...series,
            data: Object.keys(weeklyData).map(weekLabel => ({
              date: weekLabel,
              value: weeklyData[weekLabel].sum / weeklyData[weekLabel].count
            }))
          };
        });
      }
      if (this.aggregation === 30) {
        transformedData = transformedData.map(series => {
          const monthlyData = {};
          series.data.forEach(point => {
            const date = new Date(point.date);
            // Определяем начало месяца
            const monthStart = startOfMonth(date);
            const monthLabel = format(monthStart, 'LLLL yyyy', { locale: ru });

            if (!monthlyData[monthLabel]) {
              monthlyData[monthLabel] = { sum: 0, count: 0 };
            }
            monthlyData[monthLabel].sum += point.value;
            monthlyData[monthLabel].count += 1;
          });

          return {
            ...series,
            data: Object.keys(monthlyData).map(monthLabel => ({
              date: monthLabel,
              value: monthlyData[monthLabel].sum / monthlyData[monthLabel].count
            }))
          };
        });
      }
      // console.log('transformData:', transformedData);

      runInAction(() => {
        // Если данные пустые – просто пишем
        if (this.chartsData.length === 0) {
          this.chartsData = transformedData;
        } else {
          const merged = [...this.chartsData];

          transformedData.forEach(newSeries => {
            const existingSeries = merged.find(s => s.marker === newSeries.marker);
            if (existingSeries) {
              // объединяем по дате
              const existingDates = new Set(existingSeries.data.map(d => d.date));
              newSeries.data.forEach(point => {
                if (!existingDates.has(point.date)) {
                  existingSeries.data.push(point);
                }
              });
              // сортируем по дате
              existingSeries.data.sort((a, b) => new Date(a.date.split('(')[1]?.split(' - ')[0] || a.date) - new Date(b.date.split('(')[1]?.split(' - ')[0] || b.date));
            } else {
              merged.push(newSeries);
            }
          });

          this.chartsData = merged;
        }

        Object.keys(rowsByDate).forEach(label => {
          if (!this.tableDataByDate[label]) {
            this.tableDataByDate[label] = rowsByDate[label];
            return;
          }
          const existingRows = this.tableDataByDate[label];
          const existingSignatures = new Set(
            existingRows.map(row => {
              if (!row.__signature) {
                const fallback = [
                  row['ID группы'] || row.group_id || '-',
                  row['ID поста'] || row.post_id || '-',
                  row['ID комментария'] || row.comment_id || '-',
                  row['ID автора'] || row.author_id || '-',
                  row['Текст'] || row.text || '-',
                  row.date || '-',
                ].join('|');
                Object.defineProperty(row, '__signature', {
                  value: fallback,
                  enumerable: false,
                  configurable: true,
                });
                return fallback;
              }
              return row.__signature;
            })
          );
          rowsByDate[label].forEach(row => {
            let signature = row.__signature;
            if (!signature) {
              signature = [
                row['ID группы'] || row.group_id || '-',
                row['ID поста'] || row.post_id || '-',
                row['ID комментария'] || row.comment_id || '-',
                row['ID автора'] || row.author_id || '-',
                row['Текст'] || row.text || '-',
                row.date || '-',
              ].join('|');
              Object.defineProperty(row, '__signature', {
                value: signature,
                enumerable: false,
                configurable: true,
              });
            }
            if (!existingSignatures.has(signature)) {
              existingRows.push(row);
              existingSignatures.add(signature);
            }
          });
        });

        // Сохраняем end_date текущего запроса как lastEndQuery
        if (this.lastEndQuery == null || this.lastEndQuery.getTime() < endDate.getTime()) {
          this.lastEndQuery = endDate;
        }
        this.setLastQueryParams(currentParams);
        this.loading = false;
      });

    } catch (e) {
      runInAction(() => {
        this.error = 'Ошибка загрузки данных: ' + (e.response?.data?.error || e.message);
        this.chartsData = [];
        this.loading = false;
      });
    } finally {
      runInAction(() => {
        // console.log("FALSE");
        // this.loading = false;
      });
    }
  }

  getTableDataByDate(dateLabel) {
    if (!dateLabel) return [];
    const rows = this.tableDataByDate[dateLabel];
    if (!rows) return [];
    return toJS(rows);
  }
}

export const emotiveTrendsStore = new EmotiveTrendsStore();